/**
 * @file communityDownloadsApi.ts
 * @description OnBuzz Community downloads — admin-curated version list
 *   (Azure blob, anonymous read) + per-version asset list (GitHub
 *   Releases API, anonymous read).
 *
 * Why two sources:
 *   1. The blob is the admin's curated list — which versions to surface
 *      on the website + their channel. Same shape as Enterprise but a
 *      separate blob (`onbuzz-community-releases.json`).
 *   2. GitHub Releases holds the actual artifacts (the file names + URLs).
 *      Fetched per registered version. Cached in-memory for 5 min so a
 *      browser tab open all day doesn't pound the GitHub anon-rate-limit.
 *
 * Failure mode: every fetch returns a "safe empty" rather than throwing
 * so callers can render an empty state without crashing.
 */

export type ReleaseChannel = 'stable' | 'experimental';

export interface ReleaseEntry {
  version: string;
  channel: ReleaseChannel;
  addedAt?: string;
  addedBy?: string;
}

export interface ReleasesDoc {
  updatedAt: string | null;
  releases: ReleaseEntry[];
}

/**
 * One asset (file) attached to a GitHub release.
 * Only the fields we display are pulled out of the upstream shape.
 */
export interface CommunityReleaseAsset {
  name: string;
  size: number;
  downloadUrl: string;
}

/**
 * A version curated by the admin, augmented with its GitHub assets.
 * `assets` is null when the GitHub API call failed for this version
 * (rate limit, network blip, version doesn't actually exist on GitHub).
 */
export interface CommunityReleaseDetail {
  version: string;
  channel: ReleaseChannel;
  /** Direct link to the GitHub release page. */
  htmlUrl: string;
  /** Release publish time from GitHub (`published_at`), null if unknown. */
  publishedAt: string | null;
  /** Body / changelog from GitHub, truncated to keep UI tidy. */
  bodySnippet: string | null;
  /** Per-platform / per-file download links. Null on fetch failure. */
  assets: CommunityReleaseAsset[] | null;
}

const DEFAULT_BLOB_URL =
  'https://loxiaautopilotweb.blob.core.windows.net/public-releases/onbuzz-community-releases.json';
const GITHUB_API_BASE = 'https://api.github.com/repos/Loxia-ai/onbuzz-community/releases/tags';
const BODY_SNIPPET_MAX_CHARS = 400;
const GITHUB_CACHE_TTL_MS = 5 * 60 * 1000;   // 5 min

export function getCommunityReleasesBlobUrl(): string {
  return (
    (import.meta.env.VITE_COMMUNITY_RELEASES_BLOB_URL as string | undefined) ||
    DEFAULT_BLOB_URL
  );
}

/**
 * Fetch the admin-curated releases list. Returns empty document on
 * failure — never throws.
 */
export async function fetchCommunityReleases(): Promise<ReleasesDoc> {
  try {
    const res = await fetch(getCommunityReleasesBlobUrl(), { cache: 'default' });
    if (!res.ok) {
      console.warn(`[community-downloads] blob HTTP ${res.status}`);
      return { updatedAt: null, releases: [] };
    }
    const data = await res.json();
    if (!data || !Array.isArray(data.releases)) {
      return { updatedAt: null, releases: [] };
    }
    return data as ReleasesDoc;
  } catch (err) {
    console.warn('[community-downloads] fetch blob failed', err);
    return { updatedAt: null, releases: [] };
  }
}

// ── GitHub-asset cache ──────────────────────────────────────────────
// Module-level Map so multiple consumers in the same SPA session
// reuse the same fetches. Cleared on page reload.

interface CacheSlot { value: CommunityReleaseDetail; expiresAt: number; }
const _githubCache = new Map<string, CacheSlot>();   // version → slot

/**
 * Fetch a single version's GitHub release details (assets, body, etc).
 * Cached for 5 min per version.
 */
export async function fetchCommunityReleaseDetail(
  entry: ReleaseEntry,
): Promise<CommunityReleaseDetail> {
  const cached = _githubCache.get(entry.version);
  if (cached && cached.expiresAt > Date.now()) {
    return { ...cached.value, channel: entry.channel };
  }

  // GitHub stores tags as `v<version>`. The admin stores bare version.
  const tag = `v${entry.version}`;
  const url = `${GITHUB_API_BASE}/${encodeURIComponent(tag)}`;
  const fallback: CommunityReleaseDetail = {
    version: entry.version,
    channel: entry.channel,
    htmlUrl: `https://github.com/Loxia-ai/onbuzz-community/releases/tag/${tag}`,
    publishedAt: null,
    bodySnippet: null,
    assets: null,
  };

  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/vnd.github+json' },
      cache: 'default',
    });
    if (!res.ok) {
      console.warn(`[community-downloads] GitHub ${tag} HTTP ${res.status}`);
      return fallback;
    }
    const data = await res.json();
    const detail: CommunityReleaseDetail = {
      version: entry.version,
      channel: entry.channel,
      htmlUrl: typeof data.html_url === 'string' ? data.html_url : fallback.htmlUrl,
      publishedAt: typeof data.published_at === 'string' ? data.published_at : null,
      bodySnippet: typeof data.body === 'string'
        ? data.body.slice(0, BODY_SNIPPET_MAX_CHARS)
        : null,
      assets: Array.isArray(data.assets)
        ? data.assets.map((a: any) => ({
            name: String(a?.name ?? ''),
            size: typeof a?.size === 'number' ? a.size : 0,
            downloadUrl: String(a?.browser_download_url ?? ''),
          })).filter((a: CommunityReleaseAsset) => a.name && a.downloadUrl)
        : [],
    };
    _githubCache.set(entry.version, { value: detail, expiresAt: Date.now() + GITHUB_CACHE_TTL_MS });
    return detail;
  } catch (err) {
    console.warn('[community-downloads] GitHub fetch failed', err);
    return fallback;
  }
}

/**
 * Convenience: fetch the curated list + details for every entry in
 * parallel. Empty list if the blob is empty.
 */
export async function fetchAllCommunityReleaseDetails(): Promise<CommunityReleaseDetail[]> {
  const doc = await fetchCommunityReleases();
  if (doc.releases.length === 0) return [];
  return Promise.all(doc.releases.map(fetchCommunityReleaseDetail));
}

// Exposed for tests so unit tests can clear the in-flight cache.
export function _resetCommunityReleaseCacheForTests() {
  _githubCache.clear();
}
